import produce from 'immer';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import {
  getItemStringForLookupStepType,
  getItemTypeForResultsStep,
  getLookupStepInfoWithType,
} from 'lookup-config/LookupConfigUtils';
import {
  getLookupStepInfoForFlowchartProcessTaskStepItem,
  getLookupStepInfoForNonFieldStepItem,
} from 'lookup-config/LookupStepInitializerUtils';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import MenuOptionWithFloatingSubmenu from 'ui-library/menus/MenuOptionWithFloatingSubmenu';
import {defaultRenderOptionContent, getOptionRefForIndex} from 'ui-library/menus/MenuUtil';
import Option from 'ui-library/menus/Option';
import OptionContainer from 'ui-library/menus/OptionContainer';

import {
  CollapsingLookupStepInfo,
  FormErrors,
  LookupConfigModalContextInfo,
  LookupStepInfo,
  LookupStepType,
} from './LookupConfigModalConstants';
import {isFormErrorsEmpty} from './validateLookupSteps';

export interface AddLookupStepButtonMenuProps {
  handleSetLookupStep: (lookupStep: LookupStepInfo) => void;
  context?: LookupConfigModalContextInfo;
  followingStepTypes: Array<LookupStepType>;
  lastLookupStep: LookupStepInfo;
  setAndGetFormErrors: () => FormErrors;
}

export function AddLookupStepButtonMenu(props: AddLookupStepButtonMenuProps): JSX.Element {
  function onSelectOption(
    lookupStepInfoToAdd: LookupStepInfo,
    collapsingLookupStepInfos?: Array<CollapsingLookupStepInfo>
  ): void {
    const newFormErrors = props.setAndGetFormErrors();
    if (isFormErrorsEmpty(newFormErrors)) {
      let newLookupStep = lookupStepInfoToAdd;
      if (collapsingLookupStepInfos?.length) {
        newLookupStep = produce(lookupStepInfoToAdd, (draftInfo) => {
          draftInfo.collapsingLookupSteps = collapsingLookupStepInfos;
        });
      }
      props.handleSetLookupStep(newLookupStep);
    }
  }

  function getLookupStepAddButtonText(lookupStepInfo: LookupStepInfo): string {
    const containerType =
      props.lastLookupStep.lookupStep.type === LookupStepType.WELLS ? 'well' : 'container';

    switch (lookupStepInfo.lookupStep.type) {
      case LookupStepType.BOX:
        return 'Look up box of container';
      case LookupStepType.BOX_CONTAINERS:
        return 'Look up container in box';
      case LookupStepType.CONTAINER:
        return 'Look up container of entity';
      case LookupStepType.CONTENTS:
        return `Look up contents of ${containerType}`;
      case LookupStepType.PLATE:
        return `Look up plate of ${containerType}`;
      case LookupStepType.LOCATION:
        return 'Look up parent inventory location';
      case LookupStepType.WELLS:
        return 'Look up wells in plate';
      case LookupStepType.RESULTS:
        return `Look up results of ${getItemTypeForResultsStep(props.lastLookupStep)}`;
      default:
        return `Look up ${lookupStepInfo.name.toLocaleLowerCase()}`;
    }
  }

  const parentLookupStepInfo = getLookupStepInfoWithType(props.lastLookupStep) ?? props.lastLookupStep;
  const parentSchema = parentLookupStepInfo.linkSchema;
  const [optionRefs, setOptionRefs] = useState<{[ref: string]: HTMLLIElement | null}>({});

  return (
    <Menu width={Menu.WIDTHS.LARGE} height={Menu.HEIGHTS.SMALL} scrollable={true}>
      {props.followingStepTypes.map((stepType, index) => {
        let lookupStepInfo: LookupStepInfo;
        if (stepType === LookupStepType.FLOWCHART_PROCESS_TASK) {
          const flowchartTaskSchema = props.context?.flowchartTaskSchema;
          assert(flowchartTaskSchema != null, 'Expected flowchart task schema to be defined');
          lookupStepInfo = getLookupStepInfoForFlowchartProcessTaskStepItem(
            flowchartTaskSchema,
            parentSchema
          );
        } else {
          lookupStepInfo = getLookupStepInfoForNonFieldStepItem(stepType, parentSchema);
        }

        const lookupStepType = lookupStepInfo.lookupStep.type;
        if (![LookupStepType.RESULTS, LookupStepType.CONTENTS].includes(lookupStepType)) {
          return (
            <Option
              key={index}
              dataTestAttrs={{
                component: 'AddLookupStepDropdown',
                element: 'option',
                key: lookupStepType,
              }}
              onSelect={() => {
                onSelectOption(lookupStepInfo);
              }}
            >
              {getLookupStepAddButtonText(lookupStepInfo)}
            </Option>
          );
        }

        return (
          <OptionContainer
            key={index}
            dataTestAttrs={{
              component: 'AddLookupStepDropdown',
              element: 'option',
              key: lookupStepType,
            }}
            ref={(node) => {
              if (node != null) {
                const optionRefName = getOptionRefForIndex(index);
                if (optionRefs[optionRefName] == null) {
                  setOptionRefs((prevRefs) => ({
                    ...prevRefs,
                    [optionRefName]: node.getRef(),
                  }));
                }
              }
            }}
          >
            <MenuOptionWithFloatingSubmenu
              menuOptionContainer={optionRefs[getOptionRefForIndex(index)]}
              menuOptionIndex={index}
              optionData={{
                name: getLookupStepAddButtonText(lookupStepInfo),
                disabled: false,
                submenu: [],
                dataTestAttrs: {
                  component: 'AddLookupStepDropdown',
                  element: 'option',
                  key: lookupStepType,
                },
              }}
              submenuProps={{
                width: MenuWidth.LARGE,
                height: MenuHeight.SMALL,
                scrollable: true,
              }}
              renderOptionContentFn={defaultRenderOptionContent}
            >
              <Option
                dataTestAttrs={{
                  component: 'AddLookupStepDropdown',
                  element: 'option',
                  key: `${lookupStepType}-all`,
                }}
                onSelect={() => {
                  onSelectOption(lookupStepInfo);
                }}
              >
                All {getItemStringForLookupStepType(lookupStepType, true)}
              </Option>
              <Option
                dataTestAttrs={{
                  component: 'AddLookupStepDropdown',
                  element: 'option',
                  key: `${lookupStepType}-latest`,
                }}
                onSelect={() => {
                  onSelectOption(lookupStepInfo, [
                    getLookupStepInfoForNonFieldStepItem(
                      LookupStepType.LATEST,
                      parentSchema,
                      lookupStepType
                    ) as CollapsingLookupStepInfo,
                  ]);
                }}
              >
                Latest {getItemStringForLookupStepType(lookupStepType)}
              </Option>
            </MenuOptionWithFloatingSubmenu>
          </OptionContainer>
        );
      })}
    </Menu>
  );
}
